/**
 * Shared catalog tokens + Gallery navigation helpers (imported by Gallery.js).
 * Merchant UI lives in CatalogPage.js; public viewer in CatalogViewer.js.
 */

/** Max length for catalog name (must match backend CATALOG_NAME_MAX_LEN). */
export const CATALOG_NAME_MAX_LENGTH = 100;

export const catalogColors = {
  pageBg: '#F5F5F7',
  cardBg: '#FFFFFF',
  border: '#E5E7EB',
  borderActive: '#651BC6',
  activeBg: '#F3EEFF',
  textPrimary: '#1E2939',
  textSecondary: '#6A7282',
  textMuted: '#9CA3AF',
  purple: '#651BC6',
  purpleDark: '#6318C6',
  bannerBg: '#F3EDF8',
  gold: '#C4A052',
  goldDark: '#9A7B3A',
  placeholder: '#E8E4DF',
  pageOuter: '#EBEBEB',
  viewerCard: '#FFFFFF',
};

/** Public catalog viewer (Figma share page). */
export const publicViewerTheme = {
  cardRadius: 16,
  goldSerif: '"Playfair Display", "Cormorant Garamond", Georgia, serif',
  glammifyLogo: `${process.env.PUBLIC_URL || ''}/assets/img/gl-logo.svg`,
  downloadGradient: 'linear-gradient(90deg, #7B2CBF 0%, #9D4EDD 45%, #F5A623 100%)',
};

export const catalogFonts = {
  semibold: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
  medium: 'var(--font-montserrat-medium, Montserrat), sans-serif',
};

/** Underline-style MUI TextField sx (Figma edit modal). */
export const underlineFieldSx = {
  '& .MuiInputLabel-root': {
    color: catalogColors.textSecondary,
    fontSize: 13,
    fontFamily: catalogFonts.medium,
  },
  '& .MuiInput-underline:before': { borderBottomColor: catalogColors.border },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: catalogColors.purple,
  },
  '& .MuiInput-underline:after': { borderBottomColor: catalogColors.purple },
  '& .MuiInputBase-input': {
    fontFamily: catalogFonts.semibold,
    fontSize: 15,
    color: catalogColors.textPrimary,
    py: 1,
  },
};

/** Bold title on merchant product row — caption 1. */
export function formatProductLabel(item) {
  return catalogFieldDisplay(item?.caption_1);
}

/** Show stored text or NA for merchant list / details. */
export function catalogFieldDisplay(value) {
  return value && value !== 'NA' ? value : 'NA';
}

/** Caption lines for catalog product list row. */
export function getCatalogItemListLines(item) {
  return {
    caption1: catalogFieldDisplay(item?.caption_1),
    caption2: catalogFieldDisplay(item?.caption_2),
  };
}

/** Primary summary line when a single description is needed. */
export function getProductDescription(item) {
  const { caption1, caption2 } = getCatalogItemListLines(item);
  if (caption1 !== 'NA') return caption1;
  if (caption2 !== 'NA') return caption2;
  return 'No product description added';
}

/** Navigation state when picking gallery items for a catalog. */
export function buildAddToCatalogState({ catalogId, catalogName, existingGenerationIds = [] }) {
  return {
    addToCatalog: {
      catalogId,
      catalogName: catalogName || 'Catalog',
      existingGenerationIds,
      returnPath: '/catalogs',
    },
  };
}

export function parseAddToCatalogState(locationState) {
  return locationState?.addToCatalog || null;
}

/**
 * Thumbnail src for merchant catalog — backend proxy only (same as Gallery).
 * Do not use presigned S3 URLs here: SecureImage fetch() hits S3 CORS and images fail.
 */
export function getCatalogThumbSrc(item) {
  if (!item?.ai_generation_id) return null;
  const { getThumbnailUrl } = require('../../../utils/imageUrlUtils');
  const type = item.media_type === 'video' && item.has_enhanced_poster ? 'enhanced' : null;
  return getThumbnailUrl(item.ai_generation_id, 'thumbnail', type);
}

/**
 * Preview src for merchant catalog — returns original for images, thumbnail for videos.
 */
export function getCatalogPreviewSrc(item) {
  if (!item?.ai_generation_id) return null;
  const { getThumbnailUrl } = require('../../../utils/imageUrlUtils');
  const type = item.media_type === 'video' && item.has_enhanced_poster ? 'enhanced' : null;
  
  if (item.media_type === 'video') {
    return getThumbnailUrl(item.ai_generation_id, 'thumbnail', type);
  }
  
  return getThumbnailUrl(item.ai_generation_id, '1200x1200', type);
}
